import fetchCounties from "@api/operations/fetch-counties";
import fetchGeography from "@api/operations/fetch-geography";
import Country from "@api/types/country";
import { CFC } from "@api/types/cypress";
import ProfileAttribute from "@api/types/profile-attribute";
import State from "@api/types/state";
import ErrorMessage from "components/EnrolledCourseRegistrationForm/ErrorMessage";
import Label from "components/EnrolledCourseRegistrationForm/Label";
import { Fragment, useEffect, useState } from "react";

import Button from "@components/Button";
import Checkbox from "@components/EnrolledCourseRegistrationForm/Checkbox";
import InlineText from "@components/EnrolledCourseRegistrationForm/InlineText/InlineText";
import MultiCheckbox from "@components/EnrolledCourseRegistrationForm/MultiCheckbox";
import Select from "@components/EnrolledCourseRegistrationForm/Select";
import TextInput from "@components/EnrolledCourseRegistrationForm/TextInput";
import Spacer from "@components/Spacer";

const RegistrationDetail: CFC<{
  registrationFormData: ProfileAttribute[];
  attributes: any[];
  error: any;
  onChangeAttribute: (field: string, value: any) => void;
  onMultiCheckboxChange: (field: string, value: any, checked: boolean) => void;
  onBlurAttribute: (field: string, validators: any[]) => void;
  onSubmit: () => void;
}> = ({
  registrationFormData,
  attributes,
  error,
  onChangeAttribute,
  onMultiCheckboxChange,
  onBlurAttribute,
  onSubmit,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);

  useEffect(() => {
    fetchGeography().then((g) => {
      setCountries(g.countries);
      setStates(g.states);
    });
  }, []);

  const mapStateToOption = (state: State) => {
    return { label: state.name, value: state.code };
  };

  const mapCountryToOption = (country: Country) => {
    return { label: country.name, value: country.code };
  };

  const mapValueToOption = (value: string) => {
    return { label: value, value: value };
  };

  const defaultCountry = () => {
    onChangeAttribute("country", "US");
  };

  const getAttributeValue = (field: string) => {
    const attribute = attributes.find((a) => a.code === field);
    return attribute ? attribute.value : "";
  };

  // this function adds the error to the field
  const getAttributeError = (field: string) => {
    let attribute = attributes.find((a) => a.code === field);
    return attribute.error;
  };

  const getAttributeLocked = (field: string) => {
    const attribute = attributes.find((a) => a.code === field);
    return attribute.locked;
  };

  const containsMultipleCheckboxesValue = (field: string, value: string) => {
    const values = getAttributeValue(field);
    return values.filter((a: any) => a === value).length > 0;
  };

  const renderStateField = (profileAttribute: ProfileAttribute) => {
    const statesMatchingCountryCode = states
      .filter((state: State) => state.countryCode === getAttributeValue("country"))
      .map(mapStateToOption);

    return statesMatchingCountryCode.length ? (
      <Select
        field={profileAttribute.fieldName}
        options={statesMatchingCountryCode}
        value={getAttributeValue(profileAttribute.fieldName)}
        error={getAttributeError(profileAttribute.fieldName)}
        locked={getAttributeLocked(profileAttribute.fieldName)}
        onChange={onChangeAttribute}
      />
    ) : (
      <TextInput
        field={profileAttribute.fieldName}
        value={getAttributeValue(profileAttribute.fieldName)}
        error={getAttributeError(profileAttribute.fieldName)}
        locked={getAttributeLocked(profileAttribute.fieldName)}
        validators={profileAttribute.validators}
        onChange={onChangeAttribute}
        onBlur={onBlurAttribute}
      />
    );
  };

  const renderCountyField = async (profileAttribute: ProfileAttribute) => {
    const stateCode = getAttributeValue("state");
    const countryCode = getAttributeValue("country");
    const counties = await fetchCounties(stateCode, countryCode);

    return (
      <>
        {counties && counties.length > 0 ? (
          <Select
            options={counties.map(mapValueToOption)}
            field={profileAttribute.fieldName}
            value={getAttributeValue(profileAttribute.fieldName)}
            error={getAttributeError(profileAttribute.fieldName)}
            locked={getAttributeLocked(profileAttribute.fieldName)}
            onChange={onChangeAttribute}
          />
        ) : (
          <TextInput
            field={profileAttribute.fieldName}
            value={getAttributeValue(profileAttribute.fieldName)}
            error={getAttributeError(profileAttribute.fieldName)}
            locked={getAttributeLocked(profileAttribute.fieldName)}
            onChange={onChangeAttribute}
            onBlur={() => onBlurAttribute(profileAttribute.fieldName, profileAttribute.validators)}
          />
        )}
      </>
    );
  };

  const renderRegistrationFormData = (data: ProfileAttribute) => {
    return (
      <div>
        {data.type === "inlineText" && <InlineText data={data} />}
        {
          // text input or date selector
          (data.type === "text" || data.type === "dateSelector") && (
            <Fragment>
              {data.fieldName === "county" ? (
                <>
                  <Label text={data.label} required={data.required} />
                  {renderCountyField(data)}
                </>
              ) : (
                <div style={{ marginBottom: "1rem" }}>
                  {data.label && data.label.length > 0 && (
                    <Label text={data.label} required={data.required} />
                  )}
                  <TextInput
                    field={data.fieldName}
                    value={getAttributeValue(data.fieldName)}
                    error={getAttributeError(data.fieldName)}
                    locked={getAttributeLocked(data.fieldName)}
                    validators={data.validators}
                    placeholder={data.type === "dateSelector" ? "mm/dd/yyyy" : data.hint}
                    onChange={onChangeAttribute}
                    onBlur={onBlurAttribute}
                  />
                  {data.additionalText ? (
                    <div dangerouslySetInnerHTML={{ __html: data.additionalText }} />
                  ) : (
                    ""
                  )}
                  {getAttributeError(data.fieldName) && getAttributeError(data.fieldName).active && (
                    <ErrorMessage message={getAttributeError(data.fieldName).message} />
                  )}
                </div>
              )}
            </Fragment>
          )
        }
        {
          // selectors
          data.type === "select" && (
            <div style={{ marginBottom: "1rem" }}>
              <Label text={data.label} required={data.required} />
              <>
                {data.dataBinder === "stateList" ? (
                  renderStateField(data)
                ) : data.dataBinder === "countryList" ? (
                  <Select
                    field={data.fieldName}
                    options={countries.map(mapCountryToOption)}
                    value={
                      getAttributeValue(data.fieldName).length > 0
                        ? getAttributeValue(data.fieldName)
                        : defaultCountry()
                    }
                    error={getAttributeError(data.fieldName)}
                    locked={getAttributeLocked(data.fieldName)}
                    onChange={onChangeAttribute}
                  />
                ) : (
                  <Select
                    field={data.fieldName}
                    options={data.values.map(mapValueToOption)}
                    value={getAttributeValue(data.fieldName)}
                    error={getAttributeError(data.fieldName)}
                    locked={getAttributeLocked(data.fieldName)}
                    onChange={onChangeAttribute}
                  />
                )}
              </>
              {getAttributeError(data.fieldName) && getAttributeError(data.fieldName).active && (
                <ErrorMessage message={getAttributeError(data.fieldName).message} />
              )}
            </div>
          )
        }
        {
          // checkboxes
          data.type === "checkBox" && (
            <div style={{ marginBottom: "1rem" }}>
              <Checkbox
                field={data.fieldName}
                label={data.label}
                checked={getAttributeValue(data.fieldName)}
                onChange={onChangeAttribute}
              />
              {getAttributeError(data.fieldName) && getAttributeError(data.fieldName).active && (
                <ErrorMessage message={getAttributeError(data.fieldName).message} />
              )}
            </div>
          )
        }
        {
          // multiple checkboxes
          data.type === "multipleCheckboxes" && (
            <div style={{ marginBottom: "1rem" }}>
              <Label text={data.label} required={data.required} />
              {data.values.map((v: string) => (
                <MultiCheckbox
                  field={data.fieldName}
                  label={v}
                  value={v}
                  checked={containsMultipleCheckboxesValue(data.fieldName, v)}
                  onChange={onMultiCheckboxChange}
                />
              ))}
              {getAttributeError(data.fieldName) && getAttributeError(data.fieldName).active && (
                <ErrorMessage message={getAttributeError(data.fieldName).message} />
              )}
            </div>
          )
        }
      </div>
    );
  };

  const groups = [];
  registrationFormData.forEach((d) => {
    if (d.group && groups.filter((g) => g === d.group).length === 0) {
      groups.push(d.group);
    }
  });

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      {
        // render profile attributes by group first
        groups.map((g) => (
          <>
            {registrationFormData
              .filter((d) => d.group && d.group === g)
              .map((d) => renderRegistrationFormData(d))}
            {g === "Address" && (
              <>
                <Spacer size="sm" />
              </>
            )}
          </>
        ))
      }
      {
        // render profile attributes that are not in a group
        registrationFormData
          .filter((d) => !d.group || d.group.length === 0)
          .map((d) => renderRegistrationFormData(d))
      }
      <div className="save-button">
        <Button onClick={onSubmit}>Submit and Start My Course</Button>
      </div>
      {error.active && <ErrorMessage message={error.message} />}
      <style jsx>{`
        :global(.verify-address) {
          margin-bottom: 59px;
        }

        .save-button {
          width: 350px;
        }
      `}</style>
    </form>
  );
};

export default RegistrationDetail;
